import React, { FC, useMemo } from 'react';
import { View, Text } from 'react-native';
import Svg, { G, Path } from 'react-native-svg';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { isWeb } from '@helpers';
import { font } from '@styles/fonts';
import ColorWrapper from '@styles/colors';
import Separator from '@components/common/Separator';
import { useManageMachineStatusGauge } from './hooks/useManageMachineStatusGauge';
import { buildGaugeArcs } from './helpers';
import { GAUGE_CONFIG, LEGEND_CONFIG } from './constants';
import { MachineStatusGaugeProps } from './types';

const MachineStatusGaugeComponent: FC<MachineStatusGaugeProps> = ({ C }) => {
  const { segments, activeSegments, totalMachines } = useManageMachineStatusGauge({ C });

  const { size, strokeWidth, emptyArcColorKey } = GAUGE_CONFIG;
  const scaledSize = scale(size);
  const scaledStrokeWidth = scale(strokeWidth);
  const outerRadius = scaledSize / 2;
  const innerRadius = outerRadius - scaledStrokeWidth;
  const svgHeight = scaledSize / 2;

  const emptyArcColor = C(emptyArcColorKey);

  const arcs = useMemo(
    () => buildGaugeArcs({ activeSegments, totalMachines, emptyArcColor, outerRadius, innerRadius }),
    [totalMachines, activeSegments, emptyArcColor, outerRadius, innerRadius]
  );

  const centerGroupParams = isWeb ? { transform: `translate(${scaledSize / 2},${scaledSize / 2})` } : { x: scaledSize / 2, y: scaledSize / 2 };

  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
      <View style={{ width: scaledSize, height: svgHeight, position: 'relative' }}>
        <Svg width={scaledSize} height={svgHeight} style={{ overflow: 'visible' }}>
          <G {...centerGroupParams}>
            {arcs.map((arc, index) => (
              <Path key={index} d={arc.path} fill={arc.color} />
            ))}
          </G>
        </Svg>
        <View style={{ position: 'absolute', left: 0, right: 0, bottom: scale(-4), alignItems: 'center' }}>
          <Text style={[font['Large Title'], { color: C('Label/Light/Primary') }]}>{totalMachines}</Text>
          <Text style={[font.Body, { color: C('Label/Light/Tertiary') }]}>{i18n.t('Total Machines')}</Text>
        </View>
      </View>

      <View style={{ marginTop: scale(24), width: scaledSize }}>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            paddingBottom: scale(LEGEND_CONFIG.rowGap),
          }}
        >
          <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t('Machine Status')}</Text>
          <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t('Machines')}</Text>
        </View>

        {segments.map((segment) => (
          <View key={segment.name}>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingVertical: scale(LEGEND_CONFIG.rowGap),
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <View
                  style={{
                    width: scale(LEGEND_CONFIG.bulletSize),
                    height: scale(LEGEND_CONFIG.bulletSize),
                    borderRadius: scale(LEGEND_CONFIG.bulletSize / 2),
                    backgroundColor: segment.color,
                    marginRight: scale(8),
                  }}
                />
                <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary') }]}>{segment.name}</Text>
              </View>
              <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary') }]}>{segment.count}</Text>
            </View>
            <Separator C={C} />
          </View>
        ))}
      </View>
    </View>
  );
};

export const MachineStatusGauge = ColorWrapper(MachineStatusGaugeComponent);
